/**
 * First-visit welcome drawer for the login page
 *
 * Shows once per browser using localStorage. The X button dismisses
 * for the current session only; the "Let's Roll" button persists
 * the dismissal so it never shows again.
 */

import { X } from 'lucide-react';
import { useState } from 'react';
import { FrogMascot } from '@/components/FrogMascot';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from '@/components/ui/drawer';

const STORAGE_KEY = 'app-welcome-dismissed';

/** Self-contained welcome drawer that displays on first visit only */
export function AppWelcomeDrawer() {
  const [open, setOpen] = useState(() => !localStorage.getItem(STORAGE_KEY));

  /** Persist dismissal so the drawer never shows again */
  function handleDismiss() {
    localStorage.setItem(STORAGE_KEY, 'true');
    setOpen(false);
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent>
        <DrawerHeader className="relative text-center">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-8 w-8"
            onClick={() => setOpen(false)}
            aria-label="Close welcome dialog"
          >
            <X className="h-4 w-4" />
          </Button>
          <div className="flex justify-center mb-4 mt-2">
            <FrogMascot variant="welcome" size="2xl" />
          </div>
          <DrawerTitle>Welcome to Dice Frog</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 space-y-3 text-sm">
          <p>
            <span className="font-semibold">Free your dice</span>
            <span className="text-muted-foreground"> — You've got a drawer full of beautiful dice collecting dust. Roll them for real — Dice Frog catches the result and sends it to your online game.</span>
          </p>
          <p>
            <span className="font-semibold">Dice jail hits different when they're real</span>
            <span className="text-muted-foreground"> — The satisfying clink of shame. The walk to the corner. You can't get that from a random number generator.</span>
          </p>
          <p>
            <span className="font-semibold">The frog learns any die</span>
            <span className="text-muted-foreground"> — Runes, symbols, pips, homebrew chaos. Photograph your set, train the frog, and it knows them by heart. Go ahead, buy more weird dice. You have permission.</span>
          </p>
          <p>
            <span className="font-semibold">Connect once, roll everywhere</span>
            <span className="text-muted-foreground"> — One session code links your phone to Foundry VTT, Discord, or any supported platform. Setup takes seconds.</span>
          </p>
        </div>
        <DrawerFooter>
          <Button variant="accent" onClick={handleDismiss} enablePressEffect className="w-full">
            Let's Roll
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
